import { Link } from "react-router-dom";
import { ArrowRight, Wind, TrendingUp, HeartPulse, GraduationCap } from "lucide-react";

export default function WhyInflation() {
  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Why Inflation Matters</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            The silent force<br />working <span className="italic text-brass-deep">against you</span>.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            Inflation is the gradual rise in the price of goods and services — and its mirror image,
            the steady erosion of what your money can buy. It's often called a "headwind" for a
            reason: it doesn't announce itself, but it constantly slows your financial progress.
            Money that simply sits idle, or grows slower than inflation, is quietly losing real value
            every year — even if the number on your bank statement stays the same or even goes up.
          </p>
        </div>
      </section>

      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="eyebrow mb-6">What inflation looks like right now</div>
          <div className="grid md:grid-cols-2 gap-6">
            <StatBlock
              icon={<Wind size={22} />}
              headline="India — general inflation"
              value="3.9 – 4.0%"
              context="As of mid-2026, per Consumer Price Index data from India's Ministry of Statistics."
            />
            <StatBlock
              icon={<TrendingUp size={22} />}
              headline="Global average — 2026"
              value="3.6 – 3.8%"
              context="IMF estimate — varies widely by country, from near-zero to double digits."
            />
          </div>

          <div className="mt-8 card-paper p-6 md:p-8 ledger-rule">
            <div className="eyebrow mb-2">The Rule of 72</div>
            <p className="text-[15.5px] text-ink leading-relaxed">
              At a steady 6% inflation rate, prices double roughly every{" "}
              <span className="num font-medium text-brass-deep">12 years</span>. Something that costs{" "}
              <span className="num">₹10,00,000</span> today could cost roughly{" "}
              <span className="num">₹20,00,000</span> in just over a decade — even though nothing
              about the product itself has changed. If your savings aren't growing faster than this,
              you can buy less with the same money every year that passes.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-5">
              <div className="text-brass mb-3"><GraduationCap size={24} /></div>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                Education inflation —<br />a faster-moving target.
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-[15.5px] text-ink-soft leading-relaxed">
                Official CPI data tracks "education inflation" at a modest{" "}
                <span className="num text-ink">3 – 4%</span> — but this understates what families
                actually experience. Independent industry studies consistently show real-world school
                and college fee inflation in India running at{" "}
                <span className="num text-brass-deep font-medium">10 – 12% annually</span>, driven by
                private school and coaching fees that, once increased, are rarely rolled back.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <ComparisonTile label="Official CPI" value="3 – 4%" tone="soft" />
                <ComparisonTile label="Real-world" value="10 – 12%" tone="brass" />
              </div>
              <p className="text-[15.5px] text-ink-soft leading-relaxed mt-6">
                Over the last decade, education costs in India are estimated to have nearly doubled in
                real terms. A four-year professional degree costing roughly{" "}
                <span className="num text-ink">₹16,00,000</span> today could realistically cost around{" "}
                <span className="num text-brass-deep font-medium">₹40,00,000</span> in 15 years at
                these rates — a gap that catches many families off guard because it happens gradually,
                year after year.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-5">
              <div className="text-brass mb-3"><HeartPulse size={24} /></div>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                Healthcare inflation —<br />the steepest of all.
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-[15.5px] text-ink-soft leading-relaxed">
                This is where the gap between official numbers and real experience is widest.
                Official CPI health inflation is often reported as low as{" "}
                <span className="num text-ink">1.5 – 3.5%</span>, largely reflecting subsidised and
                public healthcare costs. But private medical inflation in India — hospital charges,
                surgeries, diagnostics, and specialist care — is running at an estimated{" "}
                <span className="num text-brass-deep font-medium">12 – 14% annually</span>, nearly
                three times the general inflation rate, and among the highest in Asia, compared with
                a global average medical inflation rate of roughly 9.5 – 10%.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <ComparisonTile label="Official CPI" value="1.5 – 3.5%" tone="soft" />
                <ComparisonTile label="Private medical" value="12 – 14%" tone="brick" />
              </div>
              <p className="text-[15.5px] text-ink-soft leading-relaxed mt-6">
                At this pace, healthcare costs can effectively double every five to six years —
                meaning a health insurance policy that felt adequate five years ago may now cover
                only a fraction of an actual hospital bill.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="eyebrow mb-4">What this means for your strategy</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
            Both sides of your strategy have to keep moving —<br />to stay ahead of the same headwind.
          </h2>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="card-paper p-6 ledger-rule">
              <div className="eyebrow">Investment plan</div>
              <p className="text-[15px] text-ink leading-relaxed mt-3">
                Needs to consistently outpace inflation — not just match it — which is why we build
                portfolios aimed at <em>real</em>, inflation-beating growth.
              </p>
            </div>
            <div className="card-paper p-6 ledger-rule">
              <div className="eyebrow">Protection cover</div>
              <p className="text-[15px] text-ink leading-relaxed mt-3">
                Needs to be reviewed and increased periodically, because a sum insured that looked
                sufficient a few years ago is quietly losing real value every year that healthcare
                and other costs run ahead of general inflation.
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href="https://reliservepulse.com/book-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brass"
              data-testid="inflation-cta-talk"
            >
              Talk to Us About Inflation-Proofing Your Strategy <ArrowRight size={16} />
            </a>
            <Link to="/resources" className="btn-outline" data-testid="inflation-cta-resources">
              Calculate Your Own Future Costs
            </Link>
          </div>

          <p className="mt-12 text-[12px] text-ink-soft/80 leading-relaxed max-w-4xl">
            Figures referenced above are based on data from India's Ministry of Statistics and
            Programme Implementation (MoSPI), the IMF World Economic Outlook, and industry research
            from health insurers and actuarial and consulting firms. Inflation rates are dynamic and
            are reviewed periodically as part of your strategy.
          </p>
        </div>
      </section>
    </div>
  );
}

function StatBlock({ icon, headline, value, context }) {
  return (
    <div className="card-paper p-6 md:p-7 ledger-rule">
      <div className="flex items-center gap-3 text-brass">{icon}<span className="eyebrow">Data point</span></div>
      <div className="font-display text-[19px] mt-3 text-ink">{headline}</div>
      <div className="num text-3xl md:text-4xl text-ink mt-3">{value}</div>
      <p className="text-[13px] text-ink-soft leading-relaxed mt-4">{context}</p>
    </div>
  );
}

function ComparisonTile({ label, value, tone }) {
  const color =
    tone === "brass" ? "text-brass-deep border-brass" :
    tone === "brick" ? "text-[#DC2626] border-[#DC2626]" :
    "text-ink-soft border-line";
  return (
    <div className={`p-4 border-l-2 ${color}`}>
      <div className="eyebrow !text-ink-soft">{label}</div>
      <div className={`num text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
